import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, RotateCcw } from "lucide-react";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { ErrorState } from "../components/shared/ErrorState";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { DriverFormDialog } from "../components/routes/DriverFormDialog";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { formatDate } from "../lib/utils";
import { driverStatusMeta, packingStateMeta } from "../lib/statusMaps";

function Detail({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-1 text-sm font-medium text-text-main">{value || "—"}</p>
    </div>
  );
}

export default function DriverDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, driver: null });
  const [routes, setRoutes] = useState({ loading: true, items: [] });
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reviving, setReviving] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: null, driver: null });
    try {
      const { data } = await apiClient.get(`/drivers/${id}`);
      setState({ loading: false, error: null, driver: data });
    } catch (error) {
      setState({ loading: false, error: getApiErrorMessage(error, "Could not load this driver."), driver: null });
    }
  }, [id]);

  const loadRoutes = useCallback(async () => {
    setRoutes((r) => ({ ...r, loading: true }));
    try {
      const { data } = await apiClient.get(`/drivers/${id}/routes`, { params: { limit: 50 } });
      setRoutes({ loading: false, items: data.items });
    } catch {
      setRoutes({ loading: false, items: [] });
    }
  }, [id]);

  useEffect(() => { load(); loadRoutes(); }, [load, loadRoutes]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/drivers/${id}`);
      toast.success("Driver deleted");
      navigate("/drivers");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not delete this driver."));
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleRestore = async () => {
    setReviving(true);
    try {
      await apiClient.post(`/drivers/${id}/restore`);
      toast.success("Driver revived — their login is active again");
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not revive this driver."));
    } finally {
      setReviving(false);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="driver-detail-page">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-4 h-48 w-full" />
      </div>
    );
  }

  if (state.error || !state.driver) {
    return (
      <div data-testid="driver-detail-page">
        <PageHeader title="Driver" actions={<Button variant="secondary" onClick={() => navigate("/drivers")} data-testid="driver-detail-back-button"><ArrowLeft size={16} strokeWidth={1.5} /> Back</Button>} />
        <ErrorState message={state.error || "Driver not found."} onRetry={load} />
      </div>
    );
  }

  const d = state.driver;
  const meta = driverStatusMeta(d);
  const isDeleted = d.is_deleted === 1;

  return (
    <div data-testid="driver-detail-page">
      <PageHeader
        title={d.name || `Driver #${d.id}`}
        description={`Driver ID #${d.id}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => navigate("/drivers")} data-testid="driver-detail-back-button"><ArrowLeft size={16} strokeWidth={1.5} /> Back</Button>
            {isDeleted ? (
              <Button variant="accent" onClick={handleRestore} disabled={reviving} data-testid="driver-detail-revive-button"><RotateCcw size={16} strokeWidth={1.5} /> {reviving ? "Reviving..." : "Revive Driver"}</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="driver-detail-edit-button"><Pencil size={16} strokeWidth={1.5} /> Edit</Button>
                <Button variant="danger" onClick={() => setDeleteOpen(true)} data-testid="driver-detail-delete-button"><Trash2 size={16} strokeWidth={1.5} /> Delete</Button>
              </>
            )}
          </div>
        }
      />

      <Card className="mb-6" data-testid="driver-overview-card">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Driver Details</CardTitle>
          <StatusBadge label={meta.label} variant={meta.variant} />
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Detail label="Phone" value={d.phone} testId="driver-detail-phone" />
          <Detail label="Email" value={d.email} testId="driver-detail-email" />
          <Detail label="Address" value={d.address} testId="driver-detail-address" />
          <Detail label="Endpoint Latitude" value={d.latitude} testId="driver-detail-latitude" />
          <Detail label="Endpoint Longitude" value={d.longitude} testId="driver-detail-longitude" />
        </CardContent>
      </Card>

      <h2 className="mb-2 text-sm font-semibold text-text-main">Routes</h2>
      <DataTable
        testId="driver-routes-table"
        loading={routes.loading}
        rows={routes.items}
        emptyTitle="No routes assigned yet"
        onRowClick={(row) => navigate(`/routes/${row.id}`)}
        columns={[
          { key: "id", header: "ID" },
          { key: "date", header: "Date", render: (r) => formatDate(r.date) },
          { key: "status", header: "Packing Status", render: (r) => { const m = packingStateMeta(r.packing_state); return <StatusBadge label={m.label} variant={m.variant} />; } },
          { key: "deliveries", header: "Deliveries", render: (r) => `${r.deliveries_done}/${r.deliveries_total}` },
          { key: "distance", header: "Distance", render: (r) => r.distance || "—" },
        ]}
      />

      <DriverFormDialog open={editOpen} onOpenChange={setEditOpen} driver={d} onSaved={() => { setEditOpen(false); load(); }} />
      <ConfirmDialog
        open={deleteOpen} onOpenChange={setDeleteOpen}
        title="Delete this driver?" description="This soft-deletes the driver and deactivates their login. You can revive them later. Note: deletion is blocked if they still have pending (unactioned) tiffins in the last 7 days."
        confirmLabel="Delete Driver" variant="danger" loading={deleting} onConfirm={handleDelete}
        testId="driver-delete-confirm-dialog"
      />
    </div>
  );
}
